import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface RentalLocation {
  address: string;
  coordinates: { lat: number; lng: number };
  title?: string;
}

export interface RentalSearch {
  pickupDate: string | null;
  dropOffDate: string | null;
  pickupLocation: RentalLocation | null;
  dropOffLocation: RentalLocation | null;
}

export interface RentalSummaryForm {
  mainDriver: {
    title: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    identityNumber: string;
    identityType: string;
    licenseExpiryDate: string;
  };
  billingAddress: {
    street: string;
    city: string;
    zipCode: string;
    country: string;
    fullName: string;
    company: string;
  };
  userNotes: string;
}

export interface RentalState {
  minAdvanceBookingHours: number;
  operatingHourStart: number;
  operatingHourEnd: number;
  vatRate: number;
  search: RentalSearch;
  availableCars: any[];
  unavailableCars: any[];
  totalDays: number;
  selectedCar: any | null;
  selectedInsurance: any | null;
  selectedExtras: { extraId: string; qty: number }[];
  insurances: any[];
  summaryForm: RentalSummaryForm;
  loading: boolean;
  carDetailLoading: boolean;
}

const defaultSummaryForm: RentalSummaryForm = {
  mainDriver: {
    title: "Mr",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    identityNumber: "",
    identityType: "iqama",
    licenseExpiryDate: "",
  },
  billingAddress: { street: "", city: "", zipCode: "", country: "", fullName: "", company: "" },
  userNotes: "",
};

const initialState: RentalState = {
  minAdvanceBookingHours: 6,
  operatingHourStart: 8,
  operatingHourEnd: 20,
  vatRate: 0.15,
  search: {
    pickupDate: null,
    dropOffDate: null,
    pickupLocation: null,
    dropOffLocation: null,
  },
  availableCars: [],
  unavailableCars: [],
  totalDays: 1,
  selectedCar: null,
  selectedInsurance: null,
  selectedExtras: [],
  insurances: [],
  summaryForm: defaultSummaryForm,
  loading: false,
  carDetailLoading: false,
};

const rentalSlice = createSlice({
  name: "rental",
  initialState,
  reducers: {
    setDeliveryConfig(
      state,
      action: PayloadAction<{
        minAdvanceBookingHours?: number;
        operatingHourStart?: number;
        operatingHourEnd?: number;
        vatRate?: number;
      }>
    ) {
      if (action.payload.minAdvanceBookingHours != null)
        state.minAdvanceBookingHours = action.payload.minAdvanceBookingHours;
      if (action.payload.operatingHourStart != null)
        state.operatingHourStart = action.payload.operatingHourStart;
      if (action.payload.operatingHourEnd != null)
        state.operatingHourEnd = action.payload.operatingHourEnd;
      if (action.payload.vatRate != null) state.vatRate = action.payload.vatRate;
    },
    setSearch(state, action: PayloadAction<Partial<RentalSearch>>) {
      state.search = { ...state.search, ...action.payload };
    },
    setAvailableCars(
      state,
      action: PayloadAction<{
        available: any[];
        unavailable: any[];
        totalDays: number;
        vatRate?: number;
      }>
    ) {
      state.availableCars = action.payload.available;
      state.unavailableCars = action.payload.unavailable;
      state.totalDays = action.payload.totalDays;
      if (action.payload.vatRate != null) state.vatRate = action.payload.vatRate;
    },
    setSelectedCar(state, action: PayloadAction<any>) {
      state.selectedCar = action.payload;
      if (action.payload?.vatRate != null) state.vatRate = action.payload.vatRate;
    },
    setInsurances(state, action: PayloadAction<any[]>) {
      state.insurances = action.payload;
    },
    setSelectedInsurance(state, action: PayloadAction<any | null>) {
      state.selectedInsurance = action.payload;
    },
    toggleExtra(state, action: PayloadAction<{ extraId: string; qty: number }>) {
      const { extraId, qty } = action.payload;
      const idx = state.selectedExtras.findIndex((e) => e.extraId === extraId);
      if (idx >= 0) {
        if (qty <= 0) {
          state.selectedExtras.splice(idx, 1);
        } else {
          state.selectedExtras[idx].qty = qty;
        }
      } else if (qty > 0) {
        state.selectedExtras.push({ extraId, qty });
      }
    },
    clearExtras(state) {
      state.selectedExtras = [];
    },
    setSummaryForm(state, action: PayloadAction<Partial<RentalSummaryForm>>) {
      state.summaryForm = { ...state.summaryForm, ...action.payload };
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setCarDetailLoading(state, action: PayloadAction<boolean>) {
      state.carDetailLoading = action.payload;
    },
    resetRental() {
      return { ...initialState };
    },
  },
});

export const rentalActions = rentalSlice.actions;
export default rentalSlice.reducer;
